import {
  available,
  ceilFee,
  FEE_RATE_DENOMINATOR,
  floorFee,
  isPositiveU64,
  isRate,
  isU64,
  unavailable,
} from './integer.js';
import { solveMinimumInputForOutput } from './minimum-input.js';
import { reserveRatioSpotPriceD9 } from './spot-price.js';
import type { CreatorFeeOn, PoolMathResult, SellQuoteOutput, TokenIndex } from './types.js';

export interface CpSwapPoolState {
  /** Effective quote reserve: physical vault balance minus owed fee balances. */
  quoteReserveD6: bigint;
  /** Effective outcome reserve: physical vault balance minus owed fee balances. */
  outcomeReserveD6: bigint;
  tradeFeeRateMillionths: bigint;
  creatorFeeRateMillionths: bigint;
  creatorFeeEnabled: boolean;
  creatorFeeOn: CreatorFeeOn;
  outcomeTokenIndex: TokenIndex;
  swapEnabled: boolean;
  openTimeUnixSeconds: bigint;
}

export interface CpSwapVaultReserveState {
  physicalVaultBalanceD6: bigint;
  protocolFeesOwedD6: bigint;
  fundFeesOwedD6: bigint;
  creatorFeesOwedD6: bigint;
}

export interface CpSwapPhysicalPoolReserves {
  quote: CpSwapVaultReserveState;
  outcome: CpSwapVaultReserveState;
}

export interface CpSwapEffectivePoolReserves {
  quoteReserveD6: bigint;
  outcomeReserveD6: bigint;
}

export interface CpSwapBaseInputQuote {
  inputAmountD6: bigint;
  outputAmountD6: bigint;
  curveInputAmountD6: bigint;
  curveOutputAmountD6: bigint;
  tradeFeeD6: bigint;
  creatorFeeD6: bigint;
  creatorFeeOnInput: boolean;
}

export interface CpSwapQuote extends CpSwapBaseInputQuote {
  venue: 'cp_swap';
  direction: 'buy' | 'sell';
}

export interface CpSwapBaseInput {
  inputAmountD6: bigint;
  inputReserveD6: bigint;
  outputReserveD6: bigint;
  tradeFeeRateMillionths: bigint;
  creatorFeeRateMillionths: bigint;
  creatorFeeOnInput: boolean;
}

export interface CpSwapBuyInput {
  state: CpSwapPoolState;
  quoteAmountInD6: bigint;
}

export interface CpSwapSellInput {
  state: CpSwapPoolState;
  outcomeAmountInD6: bigint;
  nowUnixSeconds: bigint;
}

export interface CpSwapSellForQuoteOutputInput {
  state: CpSwapPoolState;
  targetQuoteOutputD6: bigint;
  target: SellQuoteOutput;
  maximumOutcomeAmountInD6: bigint;
  nowUnixSeconds: bigint;
}

export interface CpSwapSellForNetQuoteOutputInput
  extends Omit<CpSwapSellForQuoteOutputInput, 'target' | 'targetQuoteOutputD6'> {
  desiredNetQuoteOutputD6: bigint;
}

export function quoteCpSwapBaseInput(input: CpSwapBaseInput): PoolMathResult<CpSwapBaseInputQuote> {
  if (!isPositiveU64(input.inputAmountD6)) return unavailable('invalid_amount');
  if (!isPositiveU64(input.inputReserveD6) || !isPositiveU64(input.outputReserveD6)) {
    return unavailable('invalid_reserves');
  }
  if (!validCpSwapFeeRates(input.tradeFeeRateMillionths, input.creatorFeeRateMillionths)) {
    return unavailable('invalid_fee_config');
  }

  let tradeFeeD6: bigint;
  let creatorFeeD6: bigint;
  let curveInputAmountD6: bigint;

  if (input.creatorFeeOnInput) {
    const combinedFeeRate = input.tradeFeeRateMillionths + input.creatorFeeRateMillionths;
    if (combinedFeeRate === 0n) return unavailable('invalid_fee_config');
    const totalFeeD6 = ceilFee(input.inputAmountD6, combinedFeeRate, FEE_RATE_DENOMINATOR);
    creatorFeeD6 =
      combinedFeeRate === 0n
        ? 0n
        : floorFee(totalFeeD6, input.creatorFeeRateMillionths, combinedFeeRate);
    tradeFeeD6 = totalFeeD6 - creatorFeeD6;
    curveInputAmountD6 = input.inputAmountD6 - totalFeeD6;
  } else {
    tradeFeeD6 = ceilFee(input.inputAmountD6, input.tradeFeeRateMillionths, FEE_RATE_DENOMINATOR);
    creatorFeeD6 = 0n;
    curveInputAmountD6 = input.inputAmountD6 - tradeFeeD6;
  }
  if (curveInputAmountD6 <= 0n) return unavailable('zero_progress');

  const curveOutputAmountD6 =
    (curveInputAmountD6 * input.outputReserveD6) / (input.inputReserveD6 + curveInputAmountD6);
  if (curveOutputAmountD6 === 0n || curveOutputAmountD6 >= input.outputReserveD6) {
    return unavailable('zero_progress');
  }

  const outputAmountD6 = input.creatorFeeOnInput
    ? curveOutputAmountD6
    : curveOutputAmountD6 -
      ceilFee(curveOutputAmountD6, input.creatorFeeRateMillionths, FEE_RATE_DENOMINATOR);
  if (!input.creatorFeeOnInput) creatorFeeD6 = curveOutputAmountD6 - outputAmountD6;
  if (outputAmountD6 <= 0n || !isU64(outputAmountD6)) return unavailable('zero_progress');

  return available({
    inputAmountD6: input.inputAmountD6,
    outputAmountD6,
    curveInputAmountD6,
    curveOutputAmountD6,
    tradeFeeD6,
    creatorFeeD6,
    creatorFeeOnInput: input.creatorFeeOnInput,
  });
}

/** Quotes from pool state only; the swap program owns execution-time availability. */
export function quoteCpSwapBuyExactIn(input: CpSwapBuyInput): PoolMathResult<CpSwapQuote> {
  const validationFailure = validateCpSwapQuoteState(input.state);
  if (validationFailure !== null) return unavailable(validationFailure);

  const quoteTokenIndex = oppositeTokenIndex(input.state.outcomeTokenIndex);
  const result = quoteCpSwapBaseInput({
    inputAmountD6: input.quoteAmountInD6,
    inputReserveD6: input.state.quoteReserveD6,
    outputReserveD6: input.state.outcomeReserveD6,
    tradeFeeRateMillionths: input.state.tradeFeeRateMillionths,
    creatorFeeRateMillionths: adjustedCreatorFeeRate(input.state),
    creatorFeeOnInput: resolveCreatorFeeOnInput(input.state.creatorFeeOn, quoteTokenIndex),
  });
  if (!result.available) return result;
  return available({ venue: 'cp_swap', direction: 'buy', ...result.value });
}

export function quoteCpSwapSellExactIn(input: CpSwapSellInput): PoolMathResult<CpSwapQuote> {
  const validationFailure = validateCpSwapExecutionState(input.state, input.nowUnixSeconds);
  if (validationFailure !== null) return unavailable(validationFailure);

  const result = quoteCpSwapBaseInput({
    inputAmountD6: input.outcomeAmountInD6,
    inputReserveD6: input.state.outcomeReserveD6,
    outputReserveD6: input.state.quoteReserveD6,
    tradeFeeRateMillionths: input.state.tradeFeeRateMillionths,
    creatorFeeRateMillionths: adjustedCreatorFeeRate(input.state),
    creatorFeeOnInput: resolveCreatorFeeOnInput(
      input.state.creatorFeeOn,
      input.state.outcomeTokenIndex,
    ),
  });
  if (!result.available) return result;
  return available({ venue: 'cp_swap', direction: 'sell', ...result.value });
}

/**
 * Values a sell against the active pool with every swap and creator fee
 * removed. This is a P&L mark, never an executable receipt.
 */
export function quoteCpSwapSellFeeExcludedExactIn(
  input: CpSwapSellInput,
): PoolMathResult<CpSwapQuote> {
  const validationFailure = validateCpSwapExecutionState(input.state, input.nowUnixSeconds);
  if (validationFailure !== null) return unavailable(validationFailure);

  const result = quoteCpSwapBaseInput({
    inputAmountD6: input.outcomeAmountInD6,
    inputReserveD6: input.state.outcomeReserveD6,
    outputReserveD6: input.state.quoteReserveD6,
    tradeFeeRateMillionths: 0n,
    creatorFeeRateMillionths: 0n,
    creatorFeeOnInput: false,
  });
  if (!result.available) return result;
  return available({ venue: 'cp_swap', direction: 'sell', ...result.value });
}

/** Finds the minimal exact-input outcome sell that reaches a gross or net quote receipt. */
export function quoteCpSwapSellForQuoteOutput(
  input: CpSwapSellForQuoteOutputInput,
): PoolMathResult<CpSwapQuote> {
  const validationFailure = validateCpSwapExecutionState(input.state, input.nowUnixSeconds);
  if (validationFailure !== null) return unavailable(validationFailure);

  return solveMinimumInputForOutput({
    targetOutputD6: input.targetQuoteOutputD6,
    maximumInputD6: input.maximumOutcomeAmountInD6,
    quoteAtInput: (outcomeAmountInD6: bigint) => {
      const quote = quoteCpSwapSellExactIn({
        state: input.state,
        outcomeAmountInD6,
        nowUnixSeconds: input.nowUnixSeconds,
      });
      if (!quote.available) return quote;
      return available({
        outputAmountD6: cpSwapSellOutput(quote.value, input.target),
        value: quote.value,
      });
    },
  });
}

export function quoteCpSwapSellForNetQuoteOutput(
  input: CpSwapSellForNetQuoteOutputInput,
): PoolMathResult<CpSwapQuote> {
  return quoteCpSwapSellForQuoteOutput({
    state: input.state,
    targetQuoteOutputD6: input.desiredNetQuoteOutputD6,
    target: 'net',
    maximumOutcomeAmountInD6: input.maximumOutcomeAmountInD6,
    nowUnixSeconds: input.nowUnixSeconds,
  });
}

export function cpSwapSpotPriceD9(state: CpSwapPoolState): PoolMathResult<bigint> {
  if (!isPositiveU64(state.quoteReserveD6) || !isPositiveU64(state.outcomeReserveD6)) {
    return unavailable('invalid_reserves');
  }
  return reserveRatioSpotPriceD9(state.quoteReserveD6, state.outcomeReserveD6);
}

export function deriveCpSwapEffectiveReserveD6(
  state: CpSwapVaultReserveState,
): PoolMathResult<bigint> {
  if (
    !isU64(state.physicalVaultBalanceD6) ||
    !isU64(state.protocolFeesOwedD6) ||
    !isU64(state.fundFeesOwedD6) ||
    !isU64(state.creatorFeesOwedD6)
  ) {
    return unavailable('invalid_reserves');
  }
  const feesOwedD6 = state.protocolFeesOwedD6 + state.fundFeesOwedD6 + state.creatorFeesOwedD6;
  if (!isU64(feesOwedD6) || feesOwedD6 > state.physicalVaultBalanceD6) {
    return unavailable('invalid_reserves');
  }
  return available(state.physicalVaultBalanceD6 - feesOwedD6);
}

export function deriveCpSwapEffectivePoolReserves(
  state: CpSwapPhysicalPoolReserves,
): PoolMathResult<CpSwapEffectivePoolReserves> {
  const quote = deriveCpSwapEffectiveReserveD6(state.quote);
  if (!quote.available) return quote;
  const outcome = deriveCpSwapEffectiveReserveD6(state.outcome);
  if (!outcome.available) return outcome;
  return available({ quoteReserveD6: quote.value, outcomeReserveD6: outcome.value });
}

export function resolveCreatorFeeOnInput(
  creatorFeeOn: CreatorFeeOn,
  inputTokenIndex: TokenIndex,
): boolean {
  return (
    creatorFeeOn === 'both' ||
    (creatorFeeOn === 'token0' && inputTokenIndex === 0) ||
    (creatorFeeOn === 'token1' && inputTokenIndex === 1)
  );
}

export function adjustedCreatorFeeRate(state: CpSwapPoolState): bigint {
  return state.creatorFeeEnabled ? state.creatorFeeRateMillionths : 0n;
}

function cpSwapSellOutput(quote: CpSwapQuote, target: SellQuoteOutput): bigint {
  return target === 'gross' ? quote.curveOutputAmountD6 : quote.outputAmountD6;
}

function oppositeTokenIndex(tokenIndex: TokenIndex): TokenIndex {
  return tokenIndex === 0 ? 1 : 0;
}

function validCpSwapFeeRates(tradeFeeRate: bigint, creatorFeeRate: bigint): boolean {
  return (
    isRate(tradeFeeRate, FEE_RATE_DENOMINATOR) &&
    isRate(creatorFeeRate, FEE_RATE_DENOMINATOR) &&
    tradeFeeRate + creatorFeeRate < FEE_RATE_DENOMINATOR
  );
}

function validateCpSwapQuoteState(
  state: CpSwapPoolState,
): 'invalid_fee_config' | 'invalid_reserves' | 'swap_disabled' | null {
  if (!isPositiveU64(state.quoteReserveD6) || !isPositiveU64(state.outcomeReserveD6)) {
    return 'invalid_reserves';
  }
  if (!validCpSwapFeeRates(state.tradeFeeRateMillionths, state.creatorFeeRateMillionths)) {
    return 'invalid_fee_config';
  }
  if (!state.swapEnabled) return 'swap_disabled';
  return null;
}

function validateCpSwapExecutionState(
  state: CpSwapPoolState,
  nowUnixSeconds: bigint,
): 'invalid_fee_config' | 'invalid_reserves' | 'pool_not_open' | 'swap_disabled' | null {
  const quoteFailure = validateCpSwapQuoteState(state);
  if (quoteFailure !== null) return quoteFailure;
  if (!isU64(state.openTimeUnixSeconds) || !isU64(nowUnixSeconds)) return 'invalid_reserves';
  if (nowUnixSeconds < state.openTimeUnixSeconds) return 'pool_not_open';
  return null;
}
